/**
 * Light / dark, persisted to localStorage and applied as a class on <html>.
 *
 * The class (rather than a data attribute) is what `@custom-variant dark` in
 * styles.css keys off, and what shadcn's own components expect.
 *
 * The same storage key is read by an inline script in index.html so the class
 * is set before first paint -- without it, reloading in dark mode flashes a
 * white page. Keep the two in sync.
 */

const STORAGE_KEY = "altura-theme";

export type Theme = "light" | "dark";

function systemTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** The stored preference, falling back to the OS setting. */
export function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // Private mode, or storage blocked by policy. Not worth failing over.
  }
  return systemTheme();
}

export function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function saveTheme(theme: Theme) {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Preference is lost on reload; the app still works.
  }
}
